import { NotificationType } from '../../../../../aumico-lib-users-fe/src/lib/enums/notification-type.enum';
import { Component, EventEmitter, Input, OnChanges, OnInit, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import {
    selectAllUserNotifications,
    selectUserNotificationsLoading,
} from '@modeso/aumico-lib-core-fe/reducers/user-notifications.reducer';
import { userNotificationsActions } from '@modeso/aumico-lib-core-fe';
import { Observable, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import type { NotificationSocketPayloadDto } from '@modeso/aumico-lib-core-fe';
import { Router } from '@angular/router';
import { SubjectType } from '../../../../../aumico-lib-cpmm-fe/src/lib/enums/subject-type.enum';
import { Paths } from '../../enums/paths.enum';
import { ProjectsOverviewActions } from '@modeso/aumico-lib-projects-fe';
import { setProjectNavigationContext } from '@modeso/aumico-lib-cpmm-fe/actions/ui.actions';
import { getCompanyById } from '@modeso/aumico-lib-companies-fe/actions/companies.actions';
import { selectSelectedCompanyById } from '@modeso/aumico-lib-companies-fe/reducer/companies.reducer';

@Component({
    selector: 'app-user-notification-page',
    templateUrl: './user-notification.page.html',
    styleUrls: ['./user-notification.page.scss'],
})
export class UserNotificationPage implements OnInit, OnChanges, OnDestroy {
    @Input() public open = false;
    @Output() public close = new EventEmitter<void>();

    public notifications$!: Observable<NotificationSocketPayloadDto[]>;
    public loading$!: Observable<boolean>;

    private currentNotifications: NotificationSocketPayloadDto[] = [];
    private subscriptions: Subscription = new Subscription();

    public constructor(
        private readonly store: Store,
        private readonly router: Router,
    ) {}

    public ngOnInit(): void {
        this.notifications$ = this.store.select(selectAllUserNotifications);
        this.loading$ = this.store.select(selectUserNotificationsLoading);
        this.subscriptions.add(
            this.notifications$.subscribe((notifications) => {
                this.currentNotifications = notifications ?? [];
            }),
        );
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['open']?.currentValue) {
            this.loadNotifications();
        }
    }

    public handleClose(): void {
        this.close.emit();
    }

    private loadNotifications(): void {
        this.store.dispatch(
            userNotificationsActions.loadUserNotifications({
                request: {
                    offset: 0,
                    limit: 100,
                    filter: { archived: false, read: undefined },
                },
            }),
        );
    }

    public handleArchive(notification: NotificationSocketPayloadDto, archive: boolean): void {
        this.store.dispatch(
            userNotificationsActions.updateNotification({
                notificationIds: [notification.id],
                update: { isArchived: archive },
            }),
        );
    }

    public handleMarkReadUnread(notification: NotificationSocketPayloadDto, read: boolean): void {
        this.store.dispatch(
            userNotificationsActions.updateNotification({
                notificationIds: [notification.id],
                update: { isRead: read },
            }),
        );
    }

    public handleOpenNotification(notification: NotificationSocketPayloadDto): void {
        const lang = this.getLanguageSegment();
        const projectId = notification?.data?.projectId;
        const companyId = notification?.data?.companyId;
        const workflowArea = notification?.data?.workflowArea;

        // REV-21374 — eine ABCP-Erwaehnung fuehrt in den ABSCHLUSS, nicht nach CPMM.
        //
        // ⚠️ Diese Abzweigung MUSS vor der `projectId`-Pruefung stehen. Eine NOTE_MENTION traegt
        // eine `projectId` (der Abschluss IST ein Projekt), und die Pruefung darunter schickt
        // alles mit `projectId` nach `…/cpmm/mandate-management` — die Erwaehnung landete auf
        // einer plausibel aussehenden falschen Seite, ohne Fehlermeldung. Deshalb ist
        // `NOTE_MENTION` auch ein eigener Typ und nicht `MENTION` mit anderen Feldern: die
        // Unterscheidung darf nicht daran haengen, welche Felder zufaellig gesetzt sind.
        const noteId = notification?.data?.noteId;
        const closingId = notification?.data?.closingId;
        if (notification?.type === NotificationType.NOTE_MENTION && noteId && closingId) {
            const segment = notification?.data?.noteKind === 'TODO' ? 'open-items' : 'comments';
            this.router.navigateByUrl(`/${lang}/home/project/${closingId}/abcp/closing/${segment}/${noteId}`);
            this.close.emit();
            return;
        }

        // REV-22155 — person task: the person project's mandate management (no closing workflow).
        if (projectId && (notification?.data as { subjectType?: string } | undefined)?.subjectType === SubjectType.PERSON) {
            const taskId = notification?.data?.taskId;
            const basePath = `/${lang}/${Paths.HOME}/${Paths.PROJECT}/${Paths.PERSON_PROJECTS}/${projectId}/${Paths.MANDATE_MANAGEMENT}`;
            // queryParams encodes the id (SRR F20).
            this.router.navigate([basePath], taskId ? { queryParams: { taskId } } : {});
            this.close.emit();
            return;
        }

        if (projectId) {
            this.store.dispatch(setProjectNavigationContext({ context: 'my-projects' }));
            this.store.dispatch(ProjectsOverviewActions.setActiveProjectId({ projectId }));
            this.store.dispatch(ProjectsOverviewActions.fetchProjectDataByProjectId({ projectId }));

            const taskId = notification?.data?.taskId;

            // MandateManagementPage will load tasks via its own combineLatest subscription
            const basePath =
                workflowArea === 'CLOSING_PROCESS'
                    ? `/${lang}/home/project/${projectId}/cpmm/closing-workflow`
                    : `/${lang}/home/project/${projectId}/cpmm/mandate-management`;
            this.router.navigate([basePath], taskId ? { queryParams: { taskId } } : {});
            this.close.emit();
            return;
        }

        if (companyId) {
            this.store.dispatch(getCompanyById({ payload: companyId }));

            this.store
                .select(selectSelectedCompanyById)
                .pipe(
                    filter((c) => !!c && c._id === companyId),
                    take(1),
                )
                .subscribe(() => {
                    this.router.navigateByUrl(`/${lang}/home/companies/${companyId}/project`);
                    this.close.emit();
                });

            return;
        }
    }

    public handleFilterChange(filterValue: string | boolean | undefined): void {
        const filter = {
            archived: filterValue === 'ARCHIVED',
            read:
                filterValue === 'READ'
                    ? true
                    : filterValue === 'UNREAD'
                    ? false
                    : undefined,
        };

        this.store.dispatch(
            userNotificationsActions.loadUserNotifications({
                request: {
                    offset: 0,
                    limit: 100,
                    filter,
                },
            }),
        );
    }

    public handleMarkAllAsRead(): void {
        const unreadIds = this.currentNotifications.filter((n) => !n.isRead).map((n) => n.id);
        if (!unreadIds.length) {
            return;
        }
        this.store.dispatch(
            userNotificationsActions.updateNotification({
                notificationIds: unreadIds,
                update: { isRead: true },
            }),
        );
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private getLanguageSegment(): string {
        const path = window?.location?.pathname ?? '';
        const firstSegment = path.split('/').filter(Boolean)[0];
        return firstSegment || 'de-ch';
    }
}
